import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { get, post } from "../../lib/http";
import { API } from "../../config/api";
import { toast } from "react-toastify";
import { rememberActiveEventId, resolveLockedEventId, offlineBookingsPath } from "../../organizer/eventContext.js";
import { formatEventDate } from "../../utils/datetime.js";

const cn = (...a) => a.filter(Boolean).join(" ");
const fmtInr = (n = 0) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const CASH_TICKET_CAP = 6;
const uuid = () =>
  "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

function sessionUser() {
  try {
    return JSON.parse(localStorage.getItem("bb_user") || "null");
  } catch {
    return null;
  }
}

function Field({ label, hint, children }) {
  return (
    <div className="flex w-full min-w-0 flex-col gap-1.5 text-left">
      {label ? (
        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {label}
        </label>
      ) : null}
      {children}
      {hint ? <p className="text-xs text-slate-500">{hint}</p> : null}
    </div>
  );
}

function Text({ label, hint, className, ...props }) {
  return (
    <Field label={label} hint={hint}>
      <input
        {...props}
        className={cn(
          "box-border h-11 w-full min-w-0 rounded-lg border border-slate-300 bg-white px-3 text-left text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 disabled:cursor-not-allowed disabled:bg-slate-100",
          className
        )}
      />
    </Field>
  );
}

function Select({ label, hint, children, className, ...props }) {
  return (
    <Field label={label} hint={hint}>
      <select
        {...props}
        className={cn(
          "box-border h-11 w-full min-w-0 cursor-pointer appearance-none rounded-lg border border-slate-300 bg-white px-3 pr-8 text-left text-sm text-slate-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200 disabled:cursor-not-allowed disabled:bg-slate-100",
          className
        )}
      >
        {children}
      </select>
    </Field>
  );
}

function Section({ title, children, action }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-slate-50/80 p-4 sm:p-5">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-sm font-bold text-slate-900">{title}</h2>
        {action || null}
      </div>
      {children}
    </section>
  );
}

function truncateText(s, max = 48) {
  const t = String(s || "").trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

function Chip({ tone = "slate", children }) {
  const tones = {
    slate: "bg-slate-50 text-slate-700 ring-slate-200",
    green: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    amber: "bg-amber-50 text-amber-700 ring-amber-200",
    red: "bg-rose-50 text-rose-700 ring-rose-200",
    blue: "bg-blue-50 text-blue-700 ring-blue-200",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${tones[tone]}`}>
      {children}
    </span>
  );
}

function SuccessModal({ open, onClose, onNew, onView, summary, complimentary }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center">
      <div onClick={onClose} className="absolute inset-0 bg-black/40" />
      <div className="relative m-0 w-full rounded-2xl bg-white shadow-xl sm:m-4 sm:w-[520px]">
        <div className="p-6">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-100">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-slate-900">
                Offline tickets issued!
              </h3>
              <p className="mt-1 text-sm text-slate-600">
                Tickets have been generated
                {summary?.buyer?.email ? <> and emailed to <b>{summary.buyer.email}</b></> : ""}.
              </p>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-lg border p-3">
              <div className="text-slate-500">Event</div>
              <div className="truncate font-semibold text-slate-900">{summary?.eventName || "-"}</div>
            </div>
            <div className="rounded-lg border p-3">
              <div className="text-slate-500">Booking Code</div>
              <div className="truncate font-semibold text-slate-900">
                {summary?.bookingCode || summary?.bookingId || "-"}
              </div>
            </div>
            <div className="rounded-lg border p-3">
              <div className="text-slate-500">Tickets</div>
              <div className="font-semibold text-slate-900">{summary?.totalTickets ?? 0}</div>
            </div>
            <div className="rounded-lg border p-3">
              <div className="text-slate-500">Amount</div>
              <div className="font-semibold text-slate-900">
                {complimentary ? "Free" : fmtInr(summary?.amount)}
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row">
            <button
              onClick={onNew}
              className="inline-flex flex-1 justify-center rounded-xl border px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Issue another
            </button>
            <button
              onClick={onView}
              className="inline-flex flex-1 justify-center rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
            >
              View booking
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function OrganizerCashBooking() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const lockedEventId = resolveLockedEventId({
    pathname: location.pathname,
    state: location.state,
    searchParams,
    params,
  });

  const me = sessionUser();

  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState(lockedEventId);
  const [event, setEvent] = useState(null);
  const [loadingEvent, setLoadingEvent] = useState(false);
  // Offline counter: show/record real category prices (not forced ₹0 guest passes)
  const complimentary = false;
  const offlineCounter = true;

  const [buyer, setBuyer] = useState({ name: "", email: "", mobile: "" });
  const [assignee, setAssignee] = useState({
    name: me?.name || "",
    email: me?.email || "",
    mobile: String(me?.mobile || me?.phone || "").replace(/\D/g, "").slice(0, 10),
  });
  const [lines, setLines] = useState([
    { categoryId: "", price: "", quantity: 1, hintSeats: null, userPriceOverride: false },
  ]);
  const [discount, setDiscount] = useState({ code: "", mode: "flat", value: "" });
  const [notes, setNotes] = useState("");
  /** bulk = one ticket for qty N (current); single = N separate tickets / barcodes */
  const [ticketIssueMode, setTicketIssueMode] = useState("bulk");
  const [idKey, setIdKey] = useState(uuid());
  const [submitting, setSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [successSummary, setSuccessSummary] = useState(null);

  const cashCap = offlineCounter
    ? Infinity
    : Math.max(1, Number(event?.maxTicketsPerUser) || CASH_TICKET_CAP);
  const totalQty = lines.reduce((s, l) => s + Number(l.quantity || 0), 0);

  const listPath = offlineBookingsPath(lockedEventId);
  const dashboardPath = lockedEventId ? `/events/dashboard/${lockedEventId}` : "/listyourshow/manage";

  useEffect(() => {
    if (lockedEventId && eventId !== lockedEventId) setEventId(lockedEventId);
  }, [lockedEventId, eventId]);

  useEffect(() => {
    if (lockedEventId) return undefined;
    let on = true;
    (async () => {
      try {
        const data = await get(`${API.events.mySubmissions}?approvalStatus=approved&limit=100`);
        const arr = data?.events || data || [];
        if (!on) return;
        setEvents(Array.isArray(arr) ? arr : []);
      } catch {
        if (on) setEvents([]);
      }
    })();
    return () => {
      on = false;
    };
  }, [lockedEventId]);

  useEffect(() => {
    if (!eventId) {
      setEvent(null);
      return undefined;
    }
    let on = true;
    (async () => {
      try {
        setLoadingEvent(true);
        const data = await get(`${API.events.getSinglEvent}/${eventId}`);
        if (!on) return;
        const ev = data?.event || data || null;
        setEvent(ev);
        const first = ev?.ticketCategories?.[0];
        setLines([
          {
            categoryId: first?.type || "",
            price: complimentary ? 0 : first?.price || "",
            quantity: 1,
            hintSeats: first?.availableSeats ?? null,
            userPriceOverride: false,
          },
        ]);
      } catch {
        if (on) setEvent(null);
      } finally {
        if (on) setLoadingEvent(false);
      }
    })();
    return () => {
      on = false;
    };
    // complimentary is applied in a separate effect so category fetch isn't retriggered by toggle
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const categories = useMemo(() => event?.ticketCategories || [], [event]);

  useEffect(() => {
    setLines((prev) => {
      let remaining = complimentary ? Infinity : cashCap;
      return prev.map((l) => {
        const evCat = categories.find((c) => c.type === l.categoryId);
        let qty = Math.max(1, Number(l.quantity || 1));
        if (!complimentary) {
          qty = Math.min(qty, remaining);
          remaining = Math.max(0, remaining - qty);
        }
        return {
          ...l,
          price: complimentary ? 0 : evCat?.price ?? l.price,
          quantity: qty,
          userPriceOverride: false,
        };
      });
    });
    if (complimentary) setDiscount({ code: "", mode: "flat", value: "" });
  }, [complimentary, categories, cashCap]);

  const updateLine = (i, patch) => {
    setLines((prev) => {
      const next = [...prev];
      const merged = { ...next[i], ...patch };
      if ("categoryId" in patch) {
        const evCat = categories.find((c) => c.type === patch.categoryId);
        merged.price = complimentary ? 0 : evCat?.price ?? "";
        merged.hintSeats = evCat?.availableSeats ?? null;
        merged.userPriceOverride = false;
      }
      if ("price" in patch) merged.userPriceOverride = true;
      if ("quantity" in patch) {
        const others = prev.reduce(
          (s, l, idx) => (idx === i ? s : s + Number(l.quantity || 0)),
          0
        );
        const seatLeft = merged.hintSeats;
        let qty = Math.max(1, Number(merged.quantity || 1));
        if (!complimentary) {
          qty = Math.min(qty, Math.max(1, cashCap - others));
        }
        if (seatLeft != null) qty = Math.min(qty, Math.max(1, Number(seatLeft)));
        merged.quantity = qty;
      }
      next[i] = merged;
      return next;
    });
  };

  const addLine = () => {
    if (!complimentary && totalQty >= cashCap) return;
    const used = new Set(lines.map((l) => l.categoryId).filter(Boolean));
    const nextCat = categories.find((c) => !used.has(c.type)) || categories[0];
    setLines((prev) => [
      ...prev,
      {
        categoryId: nextCat?.type || "",
        price: complimentary ? 0 : nextCat?.price || "",
        quantity: 1,
        hintSeats: nextCat?.availableSeats ?? null,
        userPriceOverride: false,
      },
    ]);
  };

  const removeLine = (i) => setLines((prev) => prev.filter((_, idx) => idx !== i));

  const subtotal = useMemo(
    () =>
      complimentary
        ? 0
        : lines.reduce((s, l) => s + Number(l.quantity || 0) * Number(l.price || 0), 0),
    [lines, complimentary]
  );
  const discountRaw = Number(discount.value || 0);
  const percent = discount.mode === "percent" ? Math.max(0, Math.min(100, discountRaw)) : 0;
  const discountValue = complimentary
    ? 0
    : discount.mode === "percent"
      ? Math.round((subtotal * percent) / 100)
      : Math.max(0, Math.min(subtotal, Number(discount.value || 0)));
  const total = Math.max(0, subtotal - discountValue);

  const valid =
    eventId &&
    lines.length > 0 &&
    lines.every((l) => l.categoryId && Number(l.quantity) > 0 && Number(l.price) >= 0) &&
    (complimentary || totalQty <= cashCap) &&
    ((buyer.email && /\S+@\S+\.\S+/.test(buyer.email)) ||
      (buyer.mobile && /^\d{10}$/.test(buyer.mobile)));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!valid) {
      toast.error("Please complete required fields properly.");
      return;
    }
    if (!complimentary && totalQty > cashCap) {
      toast.error(`Cash booking allows maximum ${cashCap} tickets.`);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        user: { name: buyer.name, email: buyer.email, mobile: buyer.mobile },
        assignee:
          assignee?.name || assignee?.email || assignee?.mobile
            ? { name: assignee.name, email: assignee.email, mobile: assignee.mobile }
            : undefined,
        eventId,
        categories: lines.map((l) => ({
          categoryId: l.categoryId,
          quantity: Number(l.quantity || 0),
          price: complimentary ? 0 : Number(l.price || 0),
        })),
        discount:
          !complimentary && discountValue > 0
            ? {
                code: discount.code || undefined,
                value: discountValue,
                meta: discount.mode === "percent" ? { mode: "percent", raw: discountRaw } : undefined,
              }
            : undefined,
        notes: notes || undefined,
        complimentary,
        issueType: "offline",
        ticketIssueMode, // "single" | "bulk"
        idempotencyKey: idKey,
      };

      const resp = await post(API.Payment.createCashBooking, payload);
      if (resp?.success) {
        toast.success(
          resp.idempotent
            ? "Already issued"
            : resp.ticketIssueMode === "single"
              ? `Issued ${resp.issuedCount || totalQty} single tickets`
              : "Offline tickets issued"
        );
        const booking = resp.booking || {};
        const tickets = resp.tickets || {};
        const eventName =
          booking?.eventId?.name ||
          event?.name ||
          events.find((ev) => (ev._id || ev.id) === eventId)?.name ||
          "";
        const totalTickets =
          Number(resp.issuedCount) ||
          (tickets?.ticketCategories || []).reduce(
            (sum, c) => sum + Number(c.finalQuantity || 0),
            0
          ) ||
          lines.reduce((s, l) => s + Number(l.quantity || 0), 0);

        setSuccessSummary({
          eventName,
          bookingCode: booking?.bookingCode,
          bookingId: booking?._id,
          amount: resp?.payment?.amount ?? resp?.payment?.finalAmount ?? booking?.finalAmount ?? 0,
          buyer: { name: buyer.name, email: buyer.email, mobile: buyer.mobile },
          totalTickets,
        });
        setSuccessOpen(true);
      } else {
        throw new Error(resp?.message || "Failed to issue tickets");
      }
    } catch (err) {
      toast.error(err?.message || "Something went wrong");
    } finally {
      setSubmitting(false);
      setIdKey(uuid());
    }
  };

  const resetForm = () => {
    setBuyer({ name: "", email: "", mobile: "" });
    setNotes("");
    setTicketIssueMode("bulk");
    setDiscount({ code: "", mode: "flat", value: "" });
    const first = event?.ticketCategories?.[0];
    setLines([
      {
        categoryId: first?.type || "",
        price: first?.price || "",
        quantity: 1,
        hintSeats: first?.availableSeats ?? null,
        userPriceOverride: false,
      },
    ]);
    if (!lockedEventId) {
      setEventId("");
      setEvent(null);
    }
  };

  if (
    lockedEventId &&
    location.pathname.startsWith("/listyourshow/cash-booking") &&
    !location.pathname.startsWith("/listyourshow/cash-bookings")
  ) {
    return (
      <Navigate
        to={`/events/dashboard/${lockedEventId}/cash-booking`}
        replace
        state={{ eventId: lockedEventId }}
      />
    );
  }

  if (!lockedEventId) {
    return <Navigate to="/listyourshow/manage" replace />;
  }

  return (
    <form onSubmit={onSubmit} className="space-y-5 text-left">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">
            Issue tickets
          </div>
          <h1 className="mt-0.5 text-xl font-extrabold text-slate-900 sm:text-2xl">
            Offline booking
          </h1>
          <p className="mt-1 max-w-xl text-sm text-slate-600" title={event?.name || undefined}>
            {event?.name
              ? `For ${truncateText(event.name, 56)}`
              : "Issue tickets offline for your event."}
          </p>
          <div className="mt-2">
            <Chip tone="green">Offline · no online payment</Chip>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          <Link
            to={listPath}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            Offline bookings
          </Link>
          <Link
            to={dashboardPath}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            ← Back
          </Link>
        </div>
      </div>

      <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_300px]">
        <div className="min-w-0 space-y-4">
          <Section
            title="Event"
            action={
              loadingEvent ? (
                <span className="text-xs text-slate-500">Loading…</span>
              ) : null
            }
          >
            <div className="rounded-lg border border-slate-200 bg-white px-3 py-3">
              <div
                className="truncate text-sm font-semibold text-slate-900"
                title={event?.name || undefined}
              >
                {event?.name || "Loading event…"}
              </div>
              {event && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {event.date && (
                    <Chip>{formatEventDate(event.date)}</Chip>
                  )}
                  {event.venue?.name && (
                    <Chip tone="green">{truncateText(event.venue.name, 28)}</Chip>
                  )}
                  {event.venue?.city && (
                    <Chip tone="blue">{truncateText(event.venue.city, 24)}</Chip>
                  )}
                </div>
              )}
            </div>
          </Section>

          <Section title="Guest / buyer">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <Text
                label="Full name"
                placeholder="e.g. Rohan Singh"
                value={buyer.name}
                onChange={(e) => setBuyer((b) => ({ ...b, name: e.target.value }))}
              />
              <Text
                label="Email"
                type="email"
                placeholder="you@example.com"
                value={buyer.email}
                onChange={(e) => setBuyer((b) => ({ ...b, email: e.target.value }))}
              />
              <Text
                label="Mobile"
                inputMode="numeric"
                maxLength={10}
                placeholder="10-digit mobile"
                value={buyer.mobile}
                onChange={(e) =>
                  setBuyer((b) => ({
                    ...b,
                    mobile: e.target.value.replace(/\D/g, "").slice(0, 10),
                  }))
                }
              />
            </div>
            <p className="mt-3 text-xs text-slate-500">
              Email or mobile required to deliver tickets.
            </p>
          </Section>

          <Section title="Issued by">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <Text
                label="Name"
                value={assignee.name}
                onChange={(e) => setAssignee((a) => ({ ...a, name: e.target.value }))}
              />
              <Text
                label="Email"
                type="email"
                placeholder="Optional"
                value={assignee.email}
                onChange={(e) => setAssignee((a) => ({ ...a, email: e.target.value }))}
              />
              <Text
                label="Mobile"
                inputMode="numeric"
                maxLength={10}
                placeholder="Optional"
                value={assignee.mobile}
                onChange={(e) =>
                  setAssignee((a) => ({
                    ...a,
                    mobile: e.target.value.replace(/\D/g, "").slice(0, 10),
                  }))
                }
              />
            </div>
          </Section>

          <Section title="Select tickets">
            <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => setTicketIssueMode("single")}
                className={cn(
                  "rounded-lg border px-3 py-3 text-left transition",
                  ticketIssueMode === "single"
                    ? "border-amber-500 bg-amber-50 ring-1 ring-amber-400"
                    : "border-slate-300 bg-white hover:border-amber-300"
                )}
              >
                <div className="text-sm font-semibold text-slate-900">Single ticket</div>
                <div className="mt-0.5 text-xs text-slate-500">
                  Qty N → N separate tickets (separate QR each)
                </div>
              </button>
              <button
                type="button"
                onClick={() => setTicketIssueMode("bulk")}
                className={cn(
                  "rounded-lg border px-3 py-3 text-left transition",
                  ticketIssueMode === "bulk"
                    ? "border-amber-500 bg-amber-50 ring-1 ring-amber-400"
                    : "border-slate-300 bg-white hover:border-amber-300"
                )}
              >
                <div className="text-sm font-semibold text-slate-900">Bulk ticket</div>
                <div className="mt-0.5 text-xs text-slate-500">
                  Qty N → one ticket for N people (one QR)
                </div>
              </button>
            </div>

            <div className="mb-3 flex items-center justify-between gap-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Ticket category
              </h3>
              <button
                type="button"
                onClick={addLine}
                disabled={!categories.length || (!complimentary && totalQty >= cashCap)}
                className="rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-amber-50 disabled:opacity-50"
              >
                + Add
              </button>
            </div>

            <div className="space-y-3">
              {lines.map((l, i) => {
                const evCat = categories.find((c) => c.type === l.categoryId);
                const seatLeft = evCat?.availableSeats ?? l.hintSeats;
                const others = totalQty - Number(l.quantity || 0);
                const capLeft = complimentary ? Infinity : Math.max(1, cashCap - others);
                const maxQty = Math.min(
                  seatLeft != null ? Number(seatLeft) : capLeft,
                  capLeft
                );
                const over =
                  seatLeft != null && Number(l.quantity || 0) > Number(seatLeft);
                const lineTotal = complimentary
                  ? 0
                  : Number(l.price || 0) * Number(l.quantity || 0);
                return (
                  <div
                    key={i}
                    className="rounded-lg border border-slate-200 bg-white p-3 sm:p-4"
                  >
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-12 sm:items-end">
                      <div className="sm:col-span-5">
                        <Select
                          label="Category"
                          value={l.categoryId}
                          onChange={(e) =>
                            updateLine(i, { categoryId: e.target.value })
                          }
                        >
                          <option value="">— choose —</option>
                          {categories.map((c) => (
                            <option key={c.type} value={c.type}>
                              {c.type}
                              {c.price != null ? ` · ${fmtInr(c.price)}` : ""}
                            </option>
                          ))}
                        </Select>
                      </div>
                      <div className="sm:col-span-2">
                        <Text
                          label="Price"
                          type="number"
                          min="0"
                          value={complimentary ? 0 : l.price}
                          disabled={complimentary}
                          onChange={(e) => updateLine(i, { price: e.target.value })}
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <Text
                          label="Qty"
                          type="number"
                          min="1"
                          max={Number.isFinite(maxQty) ? maxQty : undefined}
                          value={l.quantity}
                          onChange={(e) =>
                            updateLine(i, { quantity: e.target.value })
                          }
                        />
                      </div>
                      <div className="flex items-center justify-between gap-2 sm:col-span-3 sm:justify-end sm:pb-1">
                        <div className="text-sm font-bold text-slate-900">
                          {fmtInr(lineTotal)}
                        </div>
                        {lines.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeLine(i)}
                            className="rounded-lg border border-rose-200 px-2 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      {seatLeft != null && (
                        <Chip tone={over ? "red" : "slate"}>
                          {seatLeft} seats left
                        </Chip>
                      )}
                      {over && (
                        <span className="text-xs font-medium text-rose-600">
                          Not enough seats
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>

          <Section title="Notes">
            <Text
              label="Internal notes"
              placeholder="Optional — e.g. counter sale, guest list"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            {!complimentary && (
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Text
                  label="Discount code"
                  placeholder="Optional"
                  value={discount.code}
                  onChange={(e) =>
                    setDiscount((d) => ({ ...d, code: e.target.value }))
                  }
                />
                <Select
                  label="Mode"
                  value={discount.mode}
                  onChange={(e) =>
                    setDiscount((d) => ({ ...d, mode: e.target.value }))
                  }
                >
                  <option value="flat">Flat (₹)</option>
                  <option value="percent">Percent (%)</option>
                </Select>
                <Text
                  label={discount.mode === "percent" ? "Discount %" : "Discount ₹"}
                  type="number"
                  min="0"
                  value={discount.value}
                  onChange={(e) =>
                    setDiscount((d) => ({ ...d, value: e.target.value }))
                  }
                />
              </div>
            )}
          </Section>
        </div>

        <aside className="space-y-4 lg:sticky lg:top-20">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="mb-3 text-sm font-bold text-slate-900">Review</h3>
            <dl className="space-y-2.5 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">Type</dt>
                <dd className="font-semibold text-slate-900">Offline</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">Issue mode</dt>
                <dd className="font-semibold text-slate-900">
                  {ticketIssueMode === "single" ? "Single" : "Bulk"}
                </dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">Subtotal</dt>
                <dd className="font-semibold text-slate-900">{fmtInr(subtotal)}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">Discount</dt>
                <dd className="font-semibold text-emerald-700">
                  − {fmtInr(discountValue)}
                </dd>
              </div>
              <div className="flex justify-between gap-3 border-t border-slate-100 pt-2.5">
                <dt className="font-semibold text-slate-900">Total</dt>
                <dd className="text-base font-bold text-slate-900">
                  {complimentary ? "Free" : fmtInr(total)}
                </dd>
              </div>
            </dl>
            <p className="mt-3 text-xs leading-relaxed text-slate-500">
              Price is recorded on the booking. PDF emailed when buyer email is
              provided.
            </p>
            <button
              type="submit"
              disabled={!valid || submitting}
              className={cn(
                "mt-4 w-full rounded-lg bg-amber-400 px-4 py-2.5 text-sm font-bold text-slate-900 hover:bg-amber-300",
                (!valid || submitting) && "cursor-not-allowed opacity-50"
              )}
            >
              {submitting ? "Issuing…" : "Issue offline tickets"}
            </button>
          </div>

          {event?.ticketCategories?.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="mb-3 text-sm font-bold text-slate-900">Availability</h3>
              <ul className="space-y-2">
                {event.ticketCategories.map((c) => (
                  <li
                    key={c.type}
                    className="flex items-start justify-between gap-2 text-sm"
                  >
                    <span
                      className="min-w-0 flex-1 truncate text-slate-700"
                      title={c.type}
                    >
                      {c.type}
                    </span>
                    <span className="shrink-0 text-right text-xs font-semibold text-slate-600">
                      {fmtInr(c.price)} · {c.availableSeats ?? 0} left
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </div>

      <SuccessModal
        open={successOpen}
        summary={successSummary}
        complimentary={complimentary}
        onClose={() => setSuccessOpen(false)}
        onNew={() => {
          setSuccessOpen(false);
          resetForm();
        }}
        onView={() => {
          setSuccessOpen(false);
          if (successSummary?.bookingId) {
            navigate(`/listyourshow/cash-bookings/${successSummary.bookingId}`, { replace: true });
          } else {
            navigate(listPath, { replace: true });
          }
        }}
      />
    </form>
  );
}
